/**
 * AgentUpgradeTenantBanner - tenant-wide banner shown ONLY when an agent of one of the
 * tenant's BYOK clusters runs a version below the platform minimum (agent upgrade status,
 * Phase 1). Never for an optional update (UX review: passive indicators only). Owners and
 * admins only; dismissible for the browser session, and it comes back when the set of
 * affected clusters changes. The count comes from the server over ALL of the tenant's
 * clusters (GET /tenant/clusters/agent-upgrade-summary), not one list page.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/authStore'
import { useAgentUpgradeSummary } from '../hooks/useClusters'
import { UPGRADE_COMMAND_ROLES } from '../utils/agentUpgrade'

const DISMISS_KEY = 'vharta-agent-upgrade-banner-dismissed'

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY)
  } catch {
    return null
  }
}

function writeDismissed(v: string) {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, v)
  } catch {
    // storage unavailable (private window, blocked site data): the banner just comes back
  }
}

export function AgentUpgradeTenantBanner() {
  const { hasRole } = useAuth()
  const tenantId = useAuthStore((s) => s.user?.tenantId)
  const allowed = !!tenantId && hasRole([...UPGRADE_COMMAND_ROLES])
  const { data: summary } = useAgentUpgradeSummary(allowed)
  const [dismissed, setDismissed] = useState<string | null>(() => readDismissed())

  if (!allowed || !summary || summary.clusters_below === 0) return null
  const signature = [...summary.cluster_ids].sort().join(',')
  if (dismissed === signature) return null

  const next = summary.upgrades_paused
    ? ` Agent upgrades are paused by the platform${summary.paused_reason ? `: ${summary.paused_reason}` : '.'} Contact support before upgrading.`
    : ' Open the cluster for the update command.'
  return (
    <div
      className="flex items-start gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200"
      role="status"
      data-testid="agent-upgrade-tenant-banner"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="flex-1">
        {summary.clusters_below === 1 && summary.first_cluster_id ? (
          <>
            An agent on cluster{' '}
            <Link className="font-medium underline" to={`/tenant/infra/clusters/${summary.first_cluster_id}`}>
              {summary.first_cluster_name || 'this cluster'}
            </Link>{' '}
            runs a version below the platform minimum.
            {next}
          </>
        ) : (
          <>
            Agents on {summary.clusters_below} clusters run a version below the platform minimum.{' '}
            <Link className="font-medium underline" to="/tenant/infra/clusters">
              Open Clusters
            </Link>
            .{next}
          </>
        )}
      </p>
      <button
        type="button"
        className="rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40"
        aria-label="Dismiss for this session"
        onClick={() => {
          writeDismissed(signature)
          setDismissed(signature)
        }}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}
